<script>
	import { onMount } from 'svelte';

	let user = null;
	let projects = [];
	let loading = true;
	let newProjectName = '';
	let showNewProject = false;

	onMount(async () => {
		try {
			// Fetch current user
			const userResponse = await fetch('/api/auth/me');
			if (!userResponse.ok) {
				window.location.href = '/login';
				return;
			}

			user = await userResponse.json();

			// Fetch user projects
			const projectsResponse = await fetch('/api/projects');
			projects = await projectsResponse.json();
		} catch (err) {
			console.error(err);
		} finally {
			loading = false;
		}
	});

	async function createProject() {
		if (!newProjectName.trim()) return;

		try {
			const response = await fetch('/api/projects', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ name: newProjectName })
			});

			if (response.ok) {
				const newProject = await response.json();
				projects = [...projects, newProject];
				newProjectName = '';
				showNewProject = false;
			}
		} catch (err) {
			console.error(err);
		}
	}

	function logout() {
		fetch('/api/auth/logout', { method: 'POST' });
		window.location.href = '/';
	}
</script>

<svelte:head>
	<title>Dashboard - OS Studio</title>
	<meta name="description" content="Your OS Studio dashboard." />
</svelte:head>

{#if loading}
	<div class="min-h-screen flex items-center justify-center bg-gray-50">
		<div class="text-center">
			<div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
			<p class="mt-4 text-gray-600">Loading your dashboard...</p>
		</div>
	</div>
{:else if user}
	<div class="min-h-screen bg-gray-50">
		<!-- Header -->
		<div class="bg-white border-b border-gray-200 sticky top-0 z-40">
			<div class="container mx-auto px-4 max-w-6xl py-4 flex justify-between items-center">
				<div>
					<h1 class="text-2xl font-bold text-gray-900">Welcome back, {user.name}!</h1>
				</div>
				<button
					on:click={logout}
					class="px-4 py-2 text-gray-600 hover:text-gray-900 font-semibold"
				>
					Logout
				</button>
			</div>
		</div>

		<!-- Main Content -->
		<div class="container mx-auto px-4 max-w-6xl py-12">
			<!-- New Project Button -->
			<div class="mb-12">
				<button
					on:click={() => (showNewProject = !showNewProject)}
					class="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
				>
					+ Create New Project
				</button>

				{#if showNewProject}
					<div class="mt-6 bg-white rounded-lg p-6 border border-gray-200">
						<h3 class="text-lg font-bold text-gray-900 mb-4">Create a New Project</h3>
						<div class="flex gap-4">
							<input
								type="text"
								bind:value={newProjectName}
								placeholder="Project name (e.g., My API)"
								class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-600"
							/>
							<button
								on:click={createProject}
								class="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
							>
								Create
							</button>
						</div>
					</div>
				{/if}
			</div>

			<!-- Projects Grid -->
			<div>
				<h2 class="text-2xl font-bold text-gray-900 mb-6">Your Projects</h2>
				{#if projects.length === 0}
					<div class="bg-white rounded-lg border border-gray-200 p-12 text-center">
						<p class="text-gray-600 text-lg">No projects yet. Create one to get started!</p>
					</div>
				{:else}
					<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{#each projects as project (project.id)}
							<a
								href={`/dashboard/${project.id}`}
								class="bg-white rounded-lg border border-gray-200 p-6 hover:border-purple-300 hover:shadow-lg transition-all"
							>
								<h3 class="text-lg font-bold text-gray-900 mb-2">{project.name}</h3>
								<p class="text-sm text-gray-500">Created {new Date(project.createdAt).toLocaleDateString()}</p>
								<div class="mt-4 pt-4 border-t border-gray-200">
									<span class="text-sm text-purple-600 font-semibold">View Details →</span>
								</div>
							</a>
						{/each}
					</div>
				{/if}
			</div>

			<!-- Quick Start -->
			<div class="mt-20 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-8 border border-purple-200">
				<h3 class="text-2xl font-bold text-gray-900 mb-4">Quick Start Guide</h3>
				<ol class="space-y-3 text-gray-700">
					<li><strong>1.</strong> Create a new project above</li>
					<li><strong>2.</strong> Answer questions about your operating system</li>
					<li><strong>3.</strong> Review the AI-generated specification</li>
					<li><strong>4.</strong> Deploy with one click</li>
				</ol>
			</div>
		</div>
	</div>
{:else}
	<div class="min-h-screen flex items-center justify-center bg-gray-50">
		<div class="text-center">
			<p class="text-gray-600 mb-4">Please sign in to continue</p>
			<a href="/login" class="text-purple-600 hover:text-purple-700 font-semibold">Go to login</a>
		</div>
	</div>
{/if}
